import {useEffect, useRef, useState, useCallback} from 'react';
import {graphSettings} from '../helpers/configs';

/**
 * Unified hook for tracking scroll positions
 * Can track both table position and row obscurity with shared scroll listeners
 * Uses requestAnimationFrame for optimal performance
 */
let globalScrollListeners = new Map();
let globalRafId = null;
let containerScrollListeners = new Map(); // Track container listeners to avoid duplicates

const checkAllListeners = () => {
  if (globalRafId !== null) return;

  globalRafId = requestAnimationFrame(() => {
    globalScrollListeners.forEach(checkFn => checkFn());
    globalRafId = null;
  });
};

const addGlobalScrollListener = (id, checkFn) => {
  globalScrollListeners.set(id, checkFn);
  if (globalScrollListeners.size === 1) {
    // First listener - set up global scroll listeners
    window.addEventListener('scroll', checkAllListeners, {passive: true});
    window.addEventListener('resize', checkAllListeners, {passive: true});
  }
};

const removeGlobalScrollListener = id => {
  globalScrollListeners.delete(id);
  if (globalScrollListeners.size === 0) {
    // Last listener - remove global scroll listeners
    window.removeEventListener('scroll', checkAllListeners);
    window.removeEventListener('resize', checkAllListeners);
    if (globalRafId !== null) {
      cancelAnimationFrame(globalRafId);
      globalRafId = null;
    }
  }
};

const addContainerScrollListener = (container, id) => {
  if (!container) return;
  const containerId = container === window ? 'window' : `container-${container}`;

  if (!containerScrollListeners.has(containerId)) {
    container.addEventListener('scroll', checkAllListeners, {passive: true});
    containerScrollListeners.set(containerId, new Set());
  }
  containerScrollListeners.get(containerId).add(id);
};

const removeContainerScrollListener = (container, id) => {
  if (!container) return;
  const containerId = container === window ? 'window' : `container-${container}`;

  const listeners = containerScrollListeners.get(containerId);
  if (listeners) {
    listeners.delete(id);
    if (listeners.size === 0) {
      container.removeEventListener('scroll', checkAllListeners);
      containerScrollListeners.delete(containerId);
    }
  }
};

/**
 * Hook to track table position relative to viewport top
 * Returns true when table's top offset is less than threshold
 */
export function useTableScroll(tableRef, threshold = 50) {
  const [shouldEnableScroll, setShouldEnableScroll] = useState(false);
  const listenerIdRef = useRef(`table-${Math.random()}`);

  useEffect(() => {
    if (!tableRef?.current) return;

    const checkPosition = () => {
      if (!tableRef?.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      const shouldScroll = rect.top < threshold;
      setShouldEnableScroll(prev => (prev !== shouldScroll ? shouldScroll : prev));
    };

    const listenerId = listenerIdRef.current;
    addGlobalScrollListener(listenerId, checkPosition);
    addContainerScrollListener(tableRef.current, listenerId);

    // Initial check
    checkPosition();

    return () => {
      removeGlobalScrollListener(listenerId);
      removeContainerScrollListener(tableRef.current, listenerId);
    };
  }, [tableRef, threshold]);

  return shouldEnableScroll;
}

/**
 * Hook to track row position relative to viewport bottom
 * Returns [rowRef, isObscured]
 */
export function useRowObscured(shouldGraphStick, scrollContainerRef = null) {
  const rowRef = useRef(null);
  const [isObscured, setIsObscured] = useState(false);
  const listenerIdRef = useRef(`row-${Math.random()}`);

  const checkObscurity = useCallback(() => {
    if (!rowRef.current) return;
    const rect = rowRef.current.getBoundingClientRect();
    const newIsObscured =
      window.innerHeight - rect.bottom < graphSettings.wrapperHeightSticky && shouldGraphStick;
    setIsObscured(prev => (prev !== newIsObscured ? newIsObscured : prev));
  }, [shouldGraphStick]);

  useEffect(() => {
    const listenerId = listenerIdRef.current;
    addGlobalScrollListener(listenerId, checkObscurity);
    addContainerScrollListener(scrollContainerRef?.current, listenerId);

    // Initial check
    checkObscurity();

    return () => {
      removeGlobalScrollListener(listenerId);
      removeContainerScrollListener(scrollContainerRef?.current, listenerId);
    };
  }, [checkObscurity, scrollContainerRef]);

  return [rowRef, isObscured];
}

export default {useTableScroll, useRowObscured};
